import { Routes, Route } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { loadUser } from './store/slices/authSlice';

// Layouts - keep these non-lazy as they're needed immediately
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';

// Route Guards - keep these non-lazy
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';

const lazyWithChunkRetry = (importer, retryKey) =>
  lazy(() =>
    importer()
      .then((module) => {
        sessionStorage.removeItem(retryKey);
        return module;
      })
      .catch((error) => {
        const hasRetried = sessionStorage.getItem(retryKey);

        if (!hasRetried && (error?.name === 'ChunkLoadError' || /Loading chunk/i.test(error?.message || ''))) {
          sessionStorage.setItem(retryKey, '1');
          window.location.reload();
        }

        throw error;
      })
  );

// Loading component
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-beige">
    <div className="tl-spinner" aria-label="Loading" />
  </div>
);

// Public Pages - Lazy loaded
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const FabricsToSilhouette = lazy(() => import('./pages/FabricsToSilhouette'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Profile = lazy(() => import('./pages/Profile'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const ReturnPolicy = lazy(() => import('./pages/ReturnPolicy'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Faq = lazy(() => import('./pages/Faq'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Journal = lazy(() => import('./pages/Journal'));
const JournalStory = lazy(() => import('./pages/JournalStory'));

// Admin Pages - Lazy loaded (separate chunk)
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Products = lazy(() => import('./pages/admin/Products'));
const ProductForm = lazy(() => import('./pages/admin/ProductForm'));
const Categories = lazy(() => import('./pages/admin/Categories'));
const HeroSlides = lazy(() => import('./pages/admin/HeroSlides'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminOrderDetail = lazy(() => import('./pages/admin/AdminOrderDetail'));
const SalesAnalytics = lazyWithChunkRetry(
  () => import('./pages/admin/SalesAnalytics'),
  'retry-sales-analytics-chunk'
);
const Notifications = lazy(() => import('./pages/admin/Notifications'));
const Users = lazy(() => import('./pages/admin/Users'));
const ActivityLogs = lazy(() => import('./pages/admin/ActivityLogs'));
const AdminCoupons = lazy(() => import('./pages/admin/Coupons'));
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'));

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadUser());
  }, [dispatch]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<MainLayout />}>
        <Route index element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="fabrics-to-silhouette" element={<FabricsToSilhouette />} />
        <Route path="shop" element={<Shop />} />
        <Route path="shop/:category" element={<Shop />} />
        <Route path="product/:slug" element={<ProductDetail />} />
        <Route path="cart" element={<Cart />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="return-policy" element={<ReturnPolicy />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="faq" element={<Faq />} />
        <Route path="shipping-policy" element={<ShippingPolicy />} />
        <Route path="terms" element={<Terms />} />
        <Route path="journal" element={<Journal />} />
        <Route path="journal/:slug" element={<JournalStory />} />
        
        {/* Protected Client Routes */}
        <Route element={<PrivateRoute />}>
          <Route path="wishlist" element={<Wishlist />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="profile" element={<Profile />} />
          <Route path="orders" element={<Orders />} />
          <Route path="orders/:id" element={<OrderDetail />} />
        </Route>
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute />}>
        <Route element={<AdminLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="products" element={<Products />} />
          <Route path="products/new" element={<ProductForm />} />
          <Route path="products/edit/:id" element={<ProductForm />} />
          <Route path="categories" element={<Categories />} />
          <Route path="hero" element={<HeroSlides />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="analytics" element={<SalesAnalytics />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="orders/:id" element={<AdminOrderDetail />} />
          <Route path="activity" element={<ActivityLogs />} />
          <Route path="users" element={<Users />} />
          <Route path="profile" element={<AdminProfile />} />
        </Route>
      </Route>

      {/* Auth callback */}
      <Route path="auth/callback" element={<AuthCallback />} />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

export default App;
